import {useEffect, useRef} from 'react';

/**
 * Shared behaviour for the account/more popover menus: closes on Escape or a
 * click outside, and returns focus to the trigger button when it closes.
 * Returns refs to attach to the trigger button and the menu panel.
 */
const useDismissableMenu = (open, onClose) => {
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;

        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        const onPointerDown = (e) => {
            if (menuRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
            onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('mousedown', onPointerDown);

        const firstItem = menuRef.current?.querySelector('[role="menuitem"]');
        if (firstItem) firstItem.focus();

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mousedown', onPointerDown);
        };
    }, [open, onClose]);

    const wasOpen = useRef(open);
    useEffect(() => {
        if (wasOpen.current && !open && triggerRef.current) {
            triggerRef.current.focus();
        }
        wasOpen.current = open;
    }, [open]);

    return {triggerRef, menuRef};
};

export default useDismissableMenu;
